import * as Menu from "@radix-ui/react-dropdown-menu";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useWeltAktiv } from "../welt/WeltWurzel";

/** W82 Web-5c (K23, entscheide E1): ein Baustein für alle Menüs (Radix Dropdown mit Portal), nach dem Muster von `Sheet.tsx`.
 *  Das Portal hängt außerhalb der Seitenwurzel; unter einer `WeltWurzel` trägt der Inhalt darum selbst `data-welt` und
 *  `data-schwebend` und steht in der W82-Gestalt (welt/rest.css „Menü“): dichtes Glas, Ecke `--pgw-r-hinweis`, Schatten
 *  `tiefe`, Zeilen 44, Unschärfe nur, weil es über dem Inhalt schwebt (Regel 16). Ohne Welt (Kontext aus) bleibt die
 *  Schiefer-Gestalt (`mat-thick`). Fokus, Escape und Rückgabe regelt der Aufrufer wie bisher über die Radix-Rückrufe;
 *  `offen` und `onOffen` nur für Menüs ohne sichtbaren Auslöser (langer Druck auf einer Wisch-Zeile). */
export function Menue(props: {
  /** Auslöser (Radix `Trigger asChild`): ein Element, das den Fokus nehmen kann oder einen Knopf enthält */
  ausloeser: ReactNode;
  ausrichtung?: "start" | "center" | "end";
  /** Abstand zum Auslöser in px (Radix `sideOffset`) */
  abstand?: number;
  onCloseAutoFocus?: (e: Event) => void;
  onEscapeKeyDown?: (e: KeyboardEvent) => void;
  kennung?: string;
  /** Name des Menüs für Vorlesen (`aria-label`), wenn der Auslöser keinen nennt */
  name?: string;
  /** Breite und Rollen des Aufrufers (etwa `dok-mehr-menue`, `min-w-48`) */
  klasse?: string;
  offen?: boolean;
  onOffen?: (offen: boolean) => void;
  children: ReactNode;
}) {
  const welt = useWeltAktiv();
  const gesteuert = props.offen !== undefined ? { open: props.offen, onOpenChange: props.onOffen } : {};
  return (
    <Menu.Root {...gesteuert}>
      <Menu.Trigger asChild>{props.ausloeser}</Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          align={props.ausrichtung ?? "start"}
          sideOffset={props.abstand ?? 4}
          collisionPadding={8}
          aria-label={props.name}
          data-testid={props.kennung}
          data-welt={welt ? "" : undefined}
          data-schwebend={welt ? "" : undefined}
          onEscapeKeyDown={props.onEscapeKeyDown}
          onCloseAutoFocus={props.onCloseAutoFocus}
          className={`menue z-50 overflow-clip py-1 ${welt ? "menue-welt" : "mat-thick rounded-lg shadow-float"} ${props.klasse ?? ""}`}
        >
          {props.children}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}

const EINTRAG = "menue-eintrag t-body flex min-h-(--touch) cursor-default items-center gap-3 px-4 outline-none data-highlighted:bg-pg-surface-2";

/** Eintrag mit Symbol (Lucide 18); `ton="warnung"` für Löschen (Schiefer `danger`, W82 `ueberfaelligText`). Mit `href` ein
 *  Verweis (etwa „Bearbeitete Fassung laden“ mit `download`). */
export function MenueEintrag(props: {
  icon: LucideIcon;
  ton?: "warnung";
  onSelect?: (e: Event) => void;
  href?: string;
  download?: boolean;
  children: ReactNode;
}) {
  const Icon = props.icon;
  const klasse = `${EINTRAG} ${props.ton === "warnung" ? "text-pg-danger" : ""}`;
  const inhalt = (
    <>
      <Icon size={18} strokeWidth={1.75} aria-hidden />
      {props.children}
    </>
  );
  if (props.href) {
    return (
      <Menu.Item asChild onSelect={props.onSelect} data-ton={props.ton}>
        <a href={props.href} download={props.download} className={klasse}>
          {inhalt}
        </a>
      </Menu.Item>
    );
  }
  return (
    <Menu.Item onSelect={props.onSelect} data-ton={props.ton} className={klasse}>
      {inhalt}
    </Menu.Item>
  );
}

/** Trenner zwischen Gruppen (Schiefer Haarlinie, W82 `linie`). */
export function MenueTrenner() {
  return <Menu.Separator className="menue-trenner my-1 h-(--hairline) bg-pg-divider" />;
}

/** W82 Beta-W2 (D-701): Kopf eines Menüs ohne Handlung (Radix `Label`, kein Eintrag, nie fokussiert), etwa das Zeichen „Beta“
 *  im Profil-Menü am Handy. */
export function MenueKopf(props: { children: ReactNode }) {
  return <Menu.Label className="menue-kopf flex min-h-(--touch) items-center px-4">{props.children}</Menu.Label>;
}
